import { useCallback, useEffect, useRef, useState } from 'react';
import { nextShot, type Shot, type TvCue } from './tvDirector';

const TICK_MS = 1000;

export interface TvCameraShot {
  projectId: string | null;
  key: number;
}

const fitShot = (now: number): Shot => ({ projectId: null, priority: 0, startedAt: now });

/**
 * Runs the pure TV director once a second while `enabled`. Callers feed it real cues through `push`;
 * `cameraShot` changes its `key` only when the director cuts to a different shot, so the universe
 * never re-flies a shot it is already showing. Disabling or unmounting stops the timer and forgets
 * every pending cue; enabling again starts from the fit view.
 */
export function useTvDirector({
  enabled, working, planets,
}: { enabled: boolean; working: string[]; planets: Set<string> }): { cameraShot: TvCameraShot; push: (cue: TvCue) => void } {
  const state = useRef({ current: fitShot(0), pending: [] as TvCue[], lastTourIndex: -1 });
  const inputs = useRef({ working, planets });
  inputs.current = { working, planets };
  const key = useRef(0);
  const [cameraShot, setCameraShot] = useState<TvCameraShot>({ projectId: null, key: 0 });

  const push = useCallback((cue: TvCue) => {
    state.current.pending.push(cue);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    state.current = { current: fitShot(Date.now()), pending: [], lastTourIndex: -1 };
    // Entering TV fits the whole universe first.
    key.current += 1;
    setCameraShot({ projectId: null, key: key.current });
    const id = window.setInterval(() => {
      const s = state.current;
      const next = nextShot({ ...inputs.current, current: s.current, pending: s.pending, lastTourIndex: s.lastTourIndex, now: Date.now() });
      state.current = { current: next.shot, pending: next.pending, lastTourIndex: next.lastTourIndex };
      if (next.shot !== s.current) {
        key.current += 1;
        setCameraShot({ projectId: next.shot.projectId, key: key.current });
      }
    }, TICK_MS);
    return () => {
      window.clearInterval(id);
      state.current.pending = [];
    };
  }, [enabled]);

  return { cameraShot, push };
}
